import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest, authAPI } from "../../api/apiHelper";
import Button from "@mui/material/Button";
import { FiLogOut, FiUser, FiMail, FiPhone, FiShield, FiLock } from "react-icons/fi";
import useToast from "../../hooks/useToast";

export default function UserDetails() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const id = userData?.id;
  const { toast, success, error, hideToast } = useToast();

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    async function fetchUser() {
      if (!id) return;
      try {
        const data = await apiRequest("get", `/user/${id}`);
        console.log("data: ", data);
        setUser(data.data || data);
      } catch (e) {
        setUser(null);
      }
    }
    fetchUser();
  }, [id]);

  const handleLogout = () => {
    // Clear all localStorage data
    localStorage.clear();

    // Redirect to home page
    navigate("/");
  };

  // Generate initials from name
  const getInitials = (name) => {
    if (!name) return "U";
    const names = name.trim().split(" ");
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  // Handle password change
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      error("Please fill in all password fields");
      return;
    }

    if (newPassword.length < 9) {
      error("New password must be at least 9 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      error("New password and confirm password do not match");
      return;
    }

    if (currentPassword === newPassword) {
      error("New password must be different from current password");
      return;
    }

    setIsChangingPassword(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      success("Password changed successfully!");
      
      // Clear password fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Password change error:", err);
      error(err.message || "Failed to change password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div style={ { padding: "0" } }>
      {/* Header */ }
      <div style={ { marginBottom: "24px" } }>
        <h1 style={ {
          fontSize: 28,
          fontWeight: 600,
          color: "#1e293b",
          margin: 0,
          marginBottom: "0px"
        } }>
          Profile
        </h1>
        <p style={ {
          color: "#666",
          fontSize: "16px",
          margin: "0"
        } }>
          Manage your personal information and account settings
        </p>
      </div>

      {/* Profile Card */ }
      <div style={ {
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "16px",
        padding: "32px",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        maxWidth: "800px"
      } }>
        {/* Profile Header */ }
        <div style={ {
          display: "flex",
          alignItems: "center",
          gap: "24px",
          marginBottom: "32px",
          paddingBottom: "24px",
          borderBottom: "1px solid #f3f4f6"
        } }>
          {/* Profile Image */ }
          <div style={ {
            position: "relative",
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            overflow: "hidden",
            border: "3px solid #e5e7eb",
            background: user?.picture ? "transparent" : "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          } }>
            { user?.picture ? (
              <img
                src={ user.picture }
                alt="Profile"
                style={ {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                } }
                onError={ (e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                } }
              />
            ) : null }
            <div style={ {
              position: user?.picture ? "absolute" : "static",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: user?.picture ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              borderRadius: "50%"
            } }>
              <span style={ {
                color: "white",
                fontSize: "28px",
                fontWeight: "600",
                letterSpacing: "1px"
              } }>
                { getInitials(user?.name) }
              </span>
            </div>
          </div>

          {/* Profile Info */ }
          <div style={ { flex: 1 } }>
            <h2 style={ {
              fontSize: "24px",
              fontWeight: "600",
              color: "#1a1a1a",
              marginBottom: "4px"
            } }>
              { user?.name || "User" }
            </h2>
            <p style={ {
              color: "#6b7280",
              fontSize: "16px",
              margin: "0",
              marginBottom: "8px"
            } }>
              { user?.email || "No email provided" }
            </p>
            <div style={ {
              display: "inline-flex",
              alignItems: "center",
              background: user?.role === "admin" ? "#fef3c7" : user?.role === "manager" ? "#dbeafe" : "#f3e8ff",
              color: user?.role === "admin" ? "#92400e" : user?.role === "manager" ? "#1e40af" : "#7c3aed",
              padding: "4px 12px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "500",
              gap: "4px"
            } }>
              <FiShield size={ 12 } />
              { user?.role || "member" }
            </div>
          </div>
        </div>

        {/* Profile Details */ }
        <div style={ {
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "32px",
          marginBottom: "32px",
          maxWidth: "700px",
          margin: "0 auto 32px auto"
        } }>
          {/* Personal Information */ }
          <div>
            <h3 style={ {
              fontSize: "18px",
              fontWeight: "600",
              color: "#1a1a1a",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            } }>
              <FiUser size={ 18 } />
              Personal Information
            </h3>

            <div style={ { display: "flex", flexDirection: "column", gap: "16px" } }>
              <div style={ {
                padding: "16px",
                background: "#f9fafb",
                borderRadius: "8px",
                border: "1px solid #f3f4f6"
              } }>
                <div style={ {
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px"
                } }>
                  <FiUser size={ 16 } color="#6b7280" />
                  <span style={ {
                    fontSize: "14px",
                    color: "#6b7280",
                    fontWeight: "500"
                  } }>Full Name</span>
                </div>
                <span style={ {
                  fontSize: "16px",
                  color: "#1a1a1a",
                  fontWeight: "500"
                } }>
                  { user?.name || "Not provided" }
                </span>
              </div>

              <div style={ {
                padding: "16px",
                background: "#f9fafb",
                borderRadius: "8px",
                border: "1px solid #f3f4f6"
              } }>
                <div style={ {
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px"
                } }>
                  <FiMail size={ 16 } color="#6b7280" />
                  <span style={ {
                    fontSize: "14px",
                    color: "#6b7280",
                    fontWeight: "500"
                  } }>Email Address</span>
                </div>
                <span style={ {
                  fontSize: "16px",
                  color: "#1a1a1a",
                  fontWeight: "500"
                } }>
                  { user?.email || "Not provided" }
                </span>
              </div>
            </div>
          </div>

          {/* Contact & Role */ }
          <div>
            <h3 style={ {
              fontSize: "18px",
              fontWeight: "600",
              color: "#1a1a1a",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            } }>
              <FiPhone size={ 18 } />
              Contact & Role
            </h3>

            <div style={ { display: "flex", flexDirection: "column", gap: "16px" } }>
              <div style={ {
                padding: "16px",
                background: "#f9fafb",
                borderRadius: "8px",
                border: "1px solid #f3f4f6"
              } }>
                <div style={ {
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px"
                } }>
                  <FiPhone size={ 16 } color="#6b7280" />
                  <span style={ {
                    fontSize: "14px",
                    color: "#6b7280",
                    fontWeight: "500"
                  } }>Phone Number</span>
                </div>
                <span style={ {
                  fontSize: "16px",
                  color: "#1a1a1a",
                  fontWeight: "500"
                } }>
                  { user?.phoneNumber || "Not provided" }
                </span>
              </div>

              <div style={ {
                padding: "16px",
                background: "#f9fafb",
                borderRadius: "8px",
                border: "1px solid #f3f4f6"
              } }>
                <div style={ {
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px"
                } }>
                  <FiShield size={ 16 } color="#6b7280" />
                  <span style={ {
                    fontSize: "14px",
                    color: "#6b7280",
                    fontWeight: "500"
                  } }>Role</span>
                </div>
                <span style={ {
                  fontSize: "16px",
                  color: "#1a1a1a",
                  fontWeight: "500",
                  textTransform: "capitalize"
                } }>
                  { user?.role || "Member" }
                </span>
              </div>

              { user?.role !== "manager" && (
                <div style={ {
                  padding: "16px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #f3f4f6"
                } }>
                  <div style={ {
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "4px"
                  } }>
                    <FiShield size={ 16 } color="#6b7280" />
                    <span style={ {
                      fontSize: "14px",
                      color: "#6b7280",
                      fontWeight: "500"
                    } }>Team Type</span>
                  </div>
                  <span style={ {
                    fontSize: "16px",
                    color: "#1a1a1a",
                    fontWeight: "500",
                    textTransform: "capitalize"
                  } }>
                    { user?.teamType || "Not assigned" }
                  </span>
                </div>
              ) }
            </div>
          </div>
        </div>

        {/* Action Buttons */ }
        <div style={ {
          display: "flex",
          gap: "12px",
          paddingTop: "24px",
          borderTop: "1px solid #f3f4f6"
        } }>
          <Button
            variant="contained"
            onClick={ handleLogout }
            startIcon={ <FiLogOut /> }
            style={ {
              background: "#dc3545",
              color: "#fff",
              borderRadius: "8px",
              fontWeight: "600",
              textTransform: "none",
              padding: "12px 24px",
              fontSize: "14px"
            } }
            sx={ {
              "&:hover": {
                background: "#c82333",
              },
            } }
          >
            Logout
          </Button>
        </div>
      </div>

      {/* Security Section - Change Password */ }
      {/* Removed as per product requirements */ }

      {/* Toast Notification */ }
      { toast.show && (
        <div
          style={ {
            position: "fixed",
            top: "20px",
            right: "20px",
            background: toast.type === "success" ? "#10b981" : "#ef4444",
            color: "#fff",
            padding: "16px 24px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: "300px",
            animation: "slideIn 0.3s ease-out"
          } }
        >
          <span style={ { flex: 1, fontSize: "14px", fontWeight: "500" } }>
            { toast.message }
          </span>
          <button
            onClick={ hideToast }
            style={ {
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: "18px",
              padding: "0",
              lineHeight: "1"
            } }
          >
            ×
          </button>
        </div>
      ) }

      <style jsx>{ `
        @media (max-width: 768px) {
          div[style*="gridTemplateColumns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        * {
          scrollbar-width: thin;
          scrollbar-color: #d1d5db transparent;
        }

        *::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }

        *::-webkit-scrollbar-track {
          background: transparent;
        }

        *::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 0;
        }

        *::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
}